import type { RootStore } from "@state/stores/types";
import {
	DeviceDetector,
	UserTypes,
	getCurrentCulture,
	isGamesTheme,
	isUserAuthenticated,
} from "@utils";
import { StorageStateKeysEnum } from "@utils/storage";
import { action, observable } from "mobx";
import { THEME_SETTINGS } from "@theme-settings";
import BasePopupStore from "@state/stores/popups/BasePopupStore";
import { UserNotificationData } from "@api-types/user-hub/UserHubTypes";
import { RealityCheckDto } from "@data-types/reality-check";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { redirect } from "@state/utils";
import { redirectToLogin } from "@utils/auth";
import { RealityCheckApiService } from "@api-services/reality-checks/RealityCheckApiService";
import { RequestContextType } from "@utils/api-config/RequestContext";
import { TwRealityChecksApiService } from "@api-services/reality-checks/TwRealityChecksApiService";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";

class RealityCheckStore extends BasePopupStore {
	intervalId: NodeJS.Timeout | null = null;
	documentTimeStamp = new Date().getTime();

	/**
	 * iframe overlay flag is used for setting an overlay over iframes so we can track user clicks first
	 * - important for reality checks
	 * - will be set to true if document timestamp is smaller than local storage timestamp
	 */
	@observable iframeOverlay = false;

	@observable realityCheckData: RealityCheckDto | null = null;
	@observable totalTimePlayed: number = 0;
	@observable timePlayedData: { hours: number; minutes: number } = {
		hours: 0,
		minutes: 0,
	};
	@observable isGameScreenActive: boolean = false;
	@observable isPopupPending: boolean = false;
	@observable pendingRealityCheckNotification: UserNotificationData | null =
		null;

	get realityCheckTime() {
		return this.rootStore.userAuthStore.realityCheckTime;
	}

	get realityCheckBase() {
		return 10;
	}

	/**
	 * @returns reality check interval duration in seconds
	 */
	get realityCheckIntervalDuration() {
		if (
			this.rootStore.userAuthStore.user &&
			this.rootStore.userAuthStore.user?.realityCheckDuration !== null
		) {
			return this.rootStore.userAuthStore.user.realityCheckDuration * 60;
		}
		return THEME_SETTINGS.realityCheckInterval * 60;
	}

	constructor(rootStore: RootStore) {
		super(rootStore);

		if (
			!THEME_SETTINGS.isRealityCheckEnabled ||
			!THEME_SETTINGS.realityCheckInterval
		) {
			return;
		}

		this.startEventListeners();
	}

	@action.bound
	onHubRealityCheck(notification: UserNotificationData) {
		if (!THEME_SETTINGS.isRealityCheckEnabled || !isUserAuthenticated()) {
			return;
		}
		if (
			this.isActive /* || this.rootStore.automaticLogoutStore.isActive */
		) {
			return;
		}
		this.pendingRealityCheckNotification = notification;
		this.activatePopup();
	}

	//#region Popup Logic

	@action.bound
	setTotalTimePlayed(totalTimePlayed: number) {
		this.totalTimePlayed = totalTimePlayed;
	}

	@action.bound
	setRealityCheckData(realityCheckData: RealityCheckDto) {
		this.realityCheckData = realityCheckData;
	}

	@action.bound
	setTimePlayedData(timePlayedData: { hours: number; minutes: number }) {
		this.timePlayedData = timePlayedData;
	}

	@action.bound
	setIsGameScreenActive(isGameScreenActive: boolean) {
		this.isGameScreenActive = isGameScreenActive;
	}

	@action.bound
	setIsPopupPending(isPopupPending: boolean) {
		this.isPopupPending = isPopupPending;
	}

	@action.bound
	private acknowledgeRealityCheckNotification() {
		if (this.pendingRealityCheckNotification) {
			this.rootStore.userHubStore.acknowledgeNotification(
				this.pendingRealityCheckNotification
			);
			this.pendingRealityCheckNotification = null;
		}
	}

	@action.bound
	async initializeRealityCheckPopup(): Promise<void> {
		try {
			const primaryResponse =
				await RealityCheckApiService.getRealityCheckData(
					createApiRequestOptions(
						this.rootStore.userAuthStore.user?.userContextType ||
							RequestContextType.Default
					)
				);

			let responseData = new RealityCheckDto();

			let secondaryResponse = null;

			if (this.rootStore.userAuthStore.secondaryUser != null) {
				secondaryResponse =
					await RealityCheckApiService.getRealityCheckData(
						createApiRequestOptions(
							this.rootStore.userAuthStore.secondaryUser
								?.userContextType || RequestContextType.Default
						)
					);
			}

			if (this.rootStore.userAuthStore.isPrimaryUserOnline) {
				responseData = {
					casinoInfo: primaryResponse?.casinoInfo,
					casinoLiveInfo: primaryResponse?.casinoLiveInfo,
					sportBettingOnlineInfo: primaryResponse?.sportBettingInfo,
					timePlayed: primaryResponse?.timePlayed,
				};
				if (secondaryResponse != null) {
					responseData.sportBettingInfo =
						secondaryResponse.sportBettingInfo;
				}
			} else {
				responseData = {
					casinoInfo: primaryResponse?.casinoInfo,
					casinoLiveInfo: primaryResponse?.casinoLiveInfo,
					sportBettingInfo: primaryResponse?.sportBettingInfo,
					timePlayed: primaryResponse?.timePlayed,
				};
				if (secondaryResponse != null) {
					responseData.sportBettingOnlineInfo =
						secondaryResponse.sportBettingInfo;
				}
			}

			if (THEME_SETTINGS.isRealityCheckWithHeaderEnabled) {
				this.setTotalTimePlayed(
					(primaryResponse?.timePlayed ?? 0) * 60
				);
			} else {
				this.getTotalTimePlayed();
			}

			if (responseData != null) {
				this.setRealityCheckData(responseData);
				this.setTimePlayedData(
					this.convertTimeToHoursAndMinutes(this.totalTimePlayed)
				);
			}
		} catch (error) {
			console.error(error);
			this.deactivatePopup();
		}
	}

	@action.bound
	async logout(): Promise<void> {
		if (
			THEME_SETTINGS.isRealityCheckWithHeaderEnabled &&
			THEME_SETTINGS.isLogoutPopupEnabled
		) {
			this.acknowledgeRealityCheckNotification();
			await TwRealityChecksApiService.resolveRealityCheckData(
				this.rootStore.userAuthStore.user?.id || ""
			);
			this.deactivatePopup();
			this.rootStore.logoutPopupStore.activatePopup();
			return;
		}

		if (isUserAuthenticated()) {
			await this.rootStore.userAuthStore.logoutUser({
				isRealityCheckLogout: true,
			});
		}

		this.rootStore.stopSession();

		const homeUrlLink = `/${getCurrentCulture()}/${DefaultPath.HOME}`;

		if (window.location.pathname !== homeUrlLink) {
			redirect(homeUrlLink);
		}
	}

	@action.bound
	async resume(): Promise<void> {
		if (!isUserAuthenticated()) {
			await this.rootStore.userAuthStore.logoutUser();
			this.rootStore.stopSession();

			redirectToLogin("isSessionExpired=true");
		}

		if (THEME_SETTINGS.isRealityCheckWithHeaderEnabled) {
			this.acknowledgeRealityCheckNotification();

			await TwRealityChecksApiService.resolveRealityCheckData(
				this.rootStore.userAuthStore.user?.id || ""
			);

			if (isGamesTheme()) {
				this.rootStore.gamePauseStore.startGameOverlayTimer();
			}

			this.deactivatePopup();
		} else {
			//extend play time

			if (this.realityCheckTime === 0) {
				// handling for when popup opened in multiple tabs
				// resume button will extend reality check once, if reality check popup on other tabs we wont allow extending
				this.extendRealityCheckTime();

				await TwRealityChecksApiService.resolveRealityCheckData(
					this.rootStore.userAuthStore.user?.id || ""
				);
			}

			if (isGamesTheme()) {
				this.rootStore.gamePauseStore.startGameOverlayTimer();
			}

			this.startTimingSession();
			this.deactivatePopup();
		}
	}

	@action.bound
	getTotalTimePlayed() {
		const totalTimePlayed = this.totalTime;

		if (totalTimePlayed == null) {
			console.error("Total time value is null");
			return;
		}

		this.setTotalTimePlayed(totalTimePlayed);
	}

	@action.bound
	convertTimeToHoursAndMinutes(n: number): {
		hours: number;
		minutes: number;
	} {
		const totalMinutes = n / 60;
		let hours = Math.floor(totalMinutes / 60);
		let roundedMinutes = Math.round(totalMinutes % 60);

		if (roundedMinutes === 60) {
			hours++;
			roundedMinutes = 0;
		}

		return {
			hours,
			minutes: roundedMinutes,
		};
	}

	@action.bound
	popupCheck = () => {
		const isRealityCheckExpired =
			this.rootStore.userAuthStore.realityCheckTime === 0;

		const shouldShowPopup =
			THEME_SETTINGS.isRealityCheckEnabled &&
			isUserAuthenticated() &&
			isRealityCheckExpired &&
			!this.isActive &&
			!this.rootStore.automaticLogoutStore.isActive &&
			this.totalTime;

		if (shouldShowPopup) {
			if (this.isGameScreenActive) {
				this.setIsPopupPending(true);
			} else {
				this.setIsPopupPending(false);
				this.activatePopup();
			}
		} else if (!isUserAuthenticated() && this.isActive) {
			this.resetInterval();
			this.deactivatePopup();
			this.setIsPopupPending(false);
		}
	};

	@action.bound
	onRealityCheckUpdate() {
		if (this.realityCheckTime === 0) {
			this.popupCheck();
		}
	}

	//#endregion

	@action.bound
	startEventListeners() {
		window.addEventListener(
			"storage",
			this.handleActiveTimeStampStorageChange
		);
		if (DeviceDetector.isDesktopTheme) {
			window.addEventListener("click", this.onClick);
		} else {
			window.addEventListener("touchstart", this.onClick);
		}
		document.addEventListener(
			"visibilitychange",
			this.initializeRealityCheck
		);
	}

	/**
	 * used for updating last active timestamp
	 */
	@action.bound
	onClick = () => {
		if (
			this.rootStore.userAuthStore.user != null &&
			!this.isActive &&
			!this.rootStore.automaticLogoutStore.isActive
		) {
			if (!this.intervalId) {
				this.initializeRealityCheck();
			}
		} else if (
			this.isActive ||
			this.rootStore.automaticLogoutStore.isActive
		) {
			//in case one of the popups is active, we want to turn off iframe overlay on first click/touch
			this.iframeOverlay = false;
		}
	};

	/**
	 * used for generating new timestamp and if is bigger than local storage, saved to local storage and state
	 * @returns is new timestamp saved
	 */
	@action.bound
	generateNewTimeStamp() {
		const ts = new Date().getTime();
		if (this.checkIfNewestActiveDocument(ts)) {
			this.documentTimeStamp = ts;
			this.setLastActiveTimeStamp(ts);
			return true;
		}

		return false;
	}

	/**
	 * Setting iframe overlay to true in case document time stamp is smaller than local storage time stamp
	 * @param ev Storage event
	 */
	@action.bound
	handleActiveTimeStampStorageChange(ev: StorageEvent) {
		if (ev.key == StorageStateKeysEnum.LAST_ACTIVE_TIMESTAMP) {
			if (!this.checkIfNewestActiveDocument(this.documentTimeStamp)) {
				this.resetInterval();
				this.iframeOverlay = true;
			}
		}
	}

	@action.bound
	initializeRealityCheck() {
		if (THEME_SETTINGS.isRealityCheckWithHeaderEnabled) return;

		if (
			document.visibilityState == "visible" &&
			this.isUserAuthenticated()
		) {
			const isTimeStampSaved = this.generateNewTimeStamp();
			if (isTimeStampSaved) {
				this.iframeOverlay = false;
				this.startTimingSession();
			} else {
				this.pauseTimingSession();
			}
		} else {
			this.pauseTimingSession();
		}
	}

	/**
	 * @param time current datetime in timestamp
	 * @returns true if document has newer timestamp then local storage or timestamp is not defined yet in local storage
	 */
	@action.bound
	checkIfNewestActiveDocument(time: number) {
		const lastActiveTimestamp = this.getLastActiveTimeStamp();
		return (
			lastActiveTimestamp === null ||
			(lastActiveTimestamp && time >= lastActiveTimestamp)
		);
	}

	@action.bound
	pauseTimingSession() {
		this.resetInterval();
		this.popupCheck();
	}

	startTimingSession() {
		this.resetInterval();

		if (this.realityCheckTime === 0) {
			this.pauseTimingSession();
			return;
		}

		const interval = setInterval(() => {
			const currentValue = this.realityCheckTime;

			if (
				currentValue !== null &&
				currentValue > 0 &&
				this.checkIfNewestActiveDocument(this.documentTimeStamp)
			) {
				const newValue = currentValue - this.realityCheckBase;
				this.rootStore.userAuthStore.setRealityCheckTime(newValue);
				this.updateTotalTime();
				if (newValue === 0) {
					this.pauseTimingSession();
				}
			} else {
				this.pauseTimingSession();
			}
		}, 10000);

		this.intervalId = interval;
	}

	@action.bound
	extendRealityCheckTime() {
		this.rootStore.userAuthStore.setRealityCheckTime(
			this.realityCheckIntervalDuration
		);
	}

	@action.bound
	resetInterval() {
		if (this.intervalId) {
			clearInterval(this.intervalId);
		}
		this.intervalId = null;
	}

	// #region local storage getters and setters

	@action.bound
	getLastActiveTimeStamp() {
		return JSON.parse(
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.LAST_ACTIVE_TIMESTAMP
			) as string
		) as number | null;
	}

	@action.bound
	setLastActiveTimeStamp(value?: number) {
		if (value) {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.LAST_ACTIVE_TIMESTAMP,
				value
			);
			return;
		}

		this.rootStore.localStorageProvider.remove(
			StorageStateKeysEnum.LAST_ACTIVE_TIMESTAMP
		);
	}

	get totalTime() {
		return JSON.parse(
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.REALITY_CHECK_TOTAL_TIME
			) as string
		) as number | null;
	}

	@action.bound
	updateTotalTime() {
		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.REALITY_CHECK_TOTAL_TIME,
			this.totalTime
				? this.totalTime + this.realityCheckBase
				: this.realityCheckBase
		);
	}

	@action.bound
	isUserAuthenticated(activeAccount: UserTypes = UserTypes.PRIMARY) {
		// get user token direct from local storage
		if (activeAccount == UserTypes.PRIMARY) {
			return (
				this.rootStore.localStorageProvider.get(
					StorageStateKeysEnum.USER_TOKEN
				) !== null
			);
		}
		return (
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.SECONDARY_TOKEN
			) !== null
		);
	}

	// #endregion local storage getters and setters

	onDispose() {
		this.pauseTimingSession();
		this.rootStore.userAuthStore.setRealityCheckTime(null);
		this.setLastActiveTimeStamp();
	}
}

export default RealityCheckStore;
